import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { User, ShieldBan, Package, Phone, UserCheck, Pencil, FileText, Calendar, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditSaleDialog } from "@/components/edit-sale-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface CustomerProfilePageProps {
    customer: Customer;
    products: Product[];
    onBack: () => void;
    onUpdateProduct: (product: Product) => Promise<void> | void;
}

export function CustomerProfilePage({ customer, products, onBack, onUpdateProduct }: CustomerProfilePageProps) {
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [editSaleOpen, setEditSaleOpen] = useState(false);

    const customerProducts = products.filter(p => p.customerId === customer.id)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Statistics
    const totalPurchasesIQD = customerProducts.filter(p => p.currency === "IQD").reduce((acc, p) => acc + p.salePrice, 0);
    const totalPurchasesUSD = customerProducts.filter(p => p.currency === "USD").reduce((acc, p) => acc + p.salePrice, 0);
    const totalRemainingIQD = customerProducts.filter(p => p.currency === "IQD").reduce((acc, p) => acc + p.remainingAmount, 0);
    const totalRemainingUSD = customerProducts.filter(p => p.currency === "USD").reduce((acc, p) => acc + p.remainingAmount, 0);

    return (
        <div className="flex flex-col h-full space-y-4">

            {/* Profile Header Card */}
            <div className="bg-card rounded-xl border shadow-sm p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div className="flex items-center gap-6">
                        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center border-4 border-background shadow-sm ring-1 ring-border">
                            <span className="text-3xl font-bold text-primary">{customer.customerCode}</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <h1 className="text-3xl font-bold">{customer.fullName}</h1>
                                {customer.isBlocked ? (
                                    <Badge variant="destructive" className="text-sm px-2 py-0.5">
                                        <ShieldBan className="h-3.5 w-3.5 ml-1.5" />
                                        بلۆک کراو
                                    </Badge>
                                ) : (
                                    <Badge variant="default" className="text-sm px-2 py-0.5 bg-green-600 hover:bg-green-700">
                                        <UserCheck className="h-3.5 w-3.5 ml-1.5" />
                                        چالاک
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center gap-4 text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                    <Phone className="h-4 w-4" />
                                    <span dir="ltr" className="font-medium">{customer.phone1}</span>
                                </div>
                                {customer.job && (
                                    <div className="flex items-center gap-1.5 border-r pr-4 mr-1">
                                        <User className="h-4 w-4" />
                                        <span>{customer.job}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="flex gap-3 w-full md:w-auto">
                        <div className="bg-background rounded-xl p-4 border shadow-sm flex-1 md:min-w-[140px] text-center">
                            <div className="text-muted-foreground mb-1 text-sm">کۆی کڕین</div>
                            <div className="font-bold text-primary text-lg space-y-0.5">
                                {totalPurchasesUSD > 0 && <div dir="ltr">${totalPurchasesUSD.toLocaleString()}</div>}
                                {totalPurchasesIQD > 0 && <div><span dir="ltr">{totalPurchasesIQD.toLocaleString()}</span> <span className="text-xs font-normal">د.ع</span></div>}
                                {totalPurchasesUSD === 0 && totalPurchasesIQD === 0 && "0"}
                            </div>
                        </div>
                        <div className="bg-background rounded-xl p-4 border shadow-sm flex-1 md:min-w-[140px] text-center">
                            <div className="text-muted-foreground mb-1 text-sm">کۆی قەرز</div>
                            <div className="font-bold text-red-600 text-lg space-y-0.5">
                                {totalRemainingUSD > 0 && <div dir="ltr">${totalRemainingUSD.toLocaleString()}</div>}
                                {totalRemainingIQD > 0 && <div><span dir="ltr">{totalRemainingIQD.toLocaleString()}</span> <span className="text-xs font-normal">د.ع</span></div>}
                                {totalRemainingUSD === 0 && totalRemainingIQD === 0 && "0"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs Content */}
            <Tabs defaultValue="products" className="flex-1 flex flex-col" dir="rtl">
                <TabsList className="w-full justify-start h-auto p-1 bg-muted/50 rounded-lg mb-4">
                    <TabsTrigger value="products" className="flex-1 md:flex-none px-8 py-2.5">
                        کاڵا کڕاوەکان ({customerProducts.length})
                    </TabsTrigger>
                    <TabsTrigger value="info" className="flex-1 md:flex-none px-8 py-2.5">
                        زانیاری کەسی
                    </TabsTrigger>
                </TabsList>

                {/* Products Tab */}
                <TabsContent value="products" className="flex-1 space-y-4 m-0">
                    {customerProducts.length === 0 ? (
                        <div className="bg-card rounded-xl border flex flex-col items-center justify-center p-12 text-muted-foreground min-h-[300px]">
                            <Package className="h-16 w-16 opacity-20 mb-4" />
                            <p className="text-lg font-medium">هیچ کاڵایەک نەدۆزرایەوە</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Desktop Table */}
                            <div className="hidden md:block rounded-xl border bg-card overflow-hidden shadow-sm">
                                <Table>
                                    <TableHeader className="bg-muted/50">
                                        <TableRow>
                                            <TableHead className="text-right h-12">کاڵا</TableHead>
                                            <TableHead className="text-right h-12">نرخ</TableHead>
                                            <TableHead className="text-right h-12">بڕی دراو</TableHead>
                                            <TableHead className="text-right h-12">ماوە</TableHead>
                                            <TableHead className="text-right h-12">بەروار</TableHead>
                                            <TableHead className="text-right h-12 w-12"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {customerProducts.map((product) => (
                                            <TableRow key={product.id} className="hover:bg-muted/50 transition-colors">
                                                <TableCell className="font-medium py-4">
                                                    <div className="text-base">{product.deviceName}</div>
                                                    {product.deviceDescription && (
                                                        <div className="text-sm text-muted-foreground truncate max-w-[300px] mt-0.5">{product.deviceDescription}</div>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5 justify-start">
                                                        <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-green-600 font-medium">
                                                    <div className="flex items-center gap-1.5 justify-start">
                                                        <span dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-red-600 font-bold">
                                                    <div className="flex items-center gap-1.5 justify-start">
                                                        <span dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                                                        <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground" dir="ltr">
                                                    {product.date}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-9 w-9 hover:bg-primary/10 hover:text-primary transition-colors"
                                                        onClick={() => {
                                                            setEditingProduct(product);
                                                            setEditSaleOpen(true);
                                                        }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="md:hidden space-y-3">
                                {customerProducts.map((product) => (
                                    <div key={product.id} className="bg-card rounded-xl border p-4 shadow-sm">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <div className="font-bold text-lg">{product.deviceName}</div>
                                                {product.deviceDescription && (
                                                    <div className="text-sm text-muted-foreground mt-0.5">{product.deviceDescription}</div>
                                                )}
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 -mt-1 -ml-2"
                                                onClick={() => {
                                                    setEditingProduct(product);
                                                    setEditSaleOpen(true);
                                                }}
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-3 gap-3 text-sm bg-muted/40 rounded-lg p-3 mb-3">
                                            <div className="text-center">
                                                <div className="text-muted-foreground text-xs mb-1">نرخ</div>
                                                <div className="font-bold" dir="ltr">
                                                    {product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                                                </div>
                                            </div>
                                            <div className="text-center border-x border-dashed border-border">
                                                <div className="text-muted-foreground text-xs mb-1">دراو</div>
                                                <div className="font-bold text-green-600" dir="ltr">
                                                    {product.paidAmount.toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-muted-foreground text-xs mb-1">ماوە</div>
                                                <div className="font-bold text-red-600" dir="ltr">
                                                    {product.remainingAmount.toLocaleString()}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-xs text-muted-foreground px-1">
                                            <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md">
                                                <Calendar className="h-3.5 w-3.5" />
                                                <span dir="ltr">{product.date}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="bg-primary/10 text-primary px-2 py-1 rounded-md font-medium">{product.installments} قیست</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </TabsContent>

                {/* Info Tab */}
                <TabsContent value="info" className="m-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-6 bg-card rounded-xl border shadow-sm p-6">
                            <h3 className="text-lg font-bold flex items-center gap-2 border-b pb-4">
                                <User className="h-5 w-5 text-primary" />
                                زانیاری کەسی
                            </h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">ناوی تەواو</span>
                                    <span className="font-semibold text-lg">{customer.fullName}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">کۆدی کڕیار</span>
                                    <span className="font-mono font-bold text-xl text-primary">#{customer.customerCode}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">پیشە</span>
                                    <span className="font-medium">{customer.job || "-"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 bg-card rounded-xl border shadow-sm p-6">
                            <h3 className="text-lg font-bold flex items-center gap-2 border-b pb-4">
                                <Phone className="h-5 w-5 text-primary" />
                                پەیوەندی و ناونیشان
                            </h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">مۆبایل ١</span>
                                    <span className="font-bold text-lg" dir="ltr">{customer.phone1}</span>
                                </div>
                                {customer.phone2 && (
                                    <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                        <span className="text-muted-foreground">مۆبایل ٢</span>
                                        <span className="font-bold text-lg" dir="ltr">{customer.phone2}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">ناونیشان</span>
                                    <span className="font-medium text-right">{customer.address || "-"}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 bg-card rounded-xl border shadow-sm p-6">
                            <h3 className="text-lg font-bold flex items-center gap-2 border-b pb-4">
                                <UserCheck className="h-5 w-5 text-primary" />
                                زانیاری کەفیل
                            </h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">ناوی کەفیل</span>
                                    <span className="font-semibold">{customer.guarantorName || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                                    <span className="text-muted-foreground">ژمارەی کەفیل</span>
                                    <span className="font-bold text-lg" dir="ltr">{customer.guarantorPhone || "-"}</span>
                                </div>
                            </div>
                        </div>

                        {customer.notes && (
                            <div className="md:col-span-2 space-y-6 bg-card rounded-xl border shadow-sm p-6">
                                <h3 className="text-lg font-bold flex items-center gap-2 border-b pb-4">
                                    <FileText className="h-5 w-5 text-primary" />
                                    تێبینی
                                </h3>
                                <div className="p-6 bg-yellow-50 dark:bg-yellow-950/20 text-yellow-800 dark:text-yellow-200 rounded-xl border border-yellow-200 dark:border-yellow-900 leading-relaxed text-lg">
                                    {customer.notes}
                                </div>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Edit Sale Dialog */}
            <EditSaleDialog
                product={editingProduct}
                open={editSaleOpen}
                onOpenChange={(open) => {
                    setEditSaleOpen(open);
                    if (!open) {
                        setTimeout(() => setEditingProduct(null), 200);
                    }
                }}
                onUpdate={onUpdateProduct}
            />
        </div>
    );
}
